/**
 * useSandboxSession — detects whether the current authenticated session
 * is an ephemeral demo sandbox (created via POST /api/v1/create-ephemeral-sandbox).
 *
 * Persistence model:
 *  - The DemoSandboxModal stores `localStorage.sandbox_meta = {tier,expires_at,organization_id}`
 *    immediately after creating a sandbox.
 *  - Lead-capture submissions toggle `localStorage.sandbox_lead_captured = '1'`.
 *
 * Both flags are cleared on logout.
 */
import { useEffect, useState } from 'react';

const META_KEY = 'sandbox_meta';
const LEAD_KEY = 'sandbox_lead_captured';

function readMeta() {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.organization_id?.startsWith?.('TEMP_ORG_')) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function useSandboxSession() {
  const [meta, setMeta] = useState(() => readMeta());
  const [leadCaptured, setLeadCaptured] = useState(
    () => localStorage.getItem(LEAD_KEY) === '1',
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onStorage = () => {
      setMeta(readMeta());
      setLeadCaptured(localStorage.getItem(LEAD_KEY) === '1');
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('sandbox-session-changed', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('sandbox-session-changed', onStorage);
    };
  }, []);

  // 1-minute tick to keep the countdown live without rerendering everything.
  useEffect(() => {
    if (!meta) return undefined;
    const i = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(i);
  }, [meta]);

  const expiresAt = meta?.expires_at ? new Date(meta.expires_at).getTime() : 0;
  const msRemaining = Math.max(0, expiresAt - now);
  const hoursRemaining = msRemaining / (60 * 60 * 1000);

  return {
    isSandbox: !!meta,
    tier: meta?.tier || null,
    organizationId: meta?.organization_id || null,
    expiresAt: meta?.expires_at || null,
    hoursRemaining,
    msRemaining,
    leadCaptured,
    // Show 2-hour countdown banner when remaining time drops below 2h
    showExpiryBanner: !!meta && hoursRemaining > 0 && hoursRemaining <= 2,
  };
}

export function setSandboxMeta(meta) {
  try {
    if (!meta) {
      localStorage.removeItem(META_KEY);
    } else {
      localStorage.setItem(META_KEY, JSON.stringify(meta));
    }
    localStorage.removeItem(LEAD_KEY);
    window.dispatchEvent(new Event('sandbox-session-changed'));
  } catch {}
}

export function markSandboxLeadCaptured() {
  try {
    localStorage.setItem(LEAD_KEY, '1');
    window.dispatchEvent(new Event('sandbox-session-changed'));
  } catch {}
}

export function clearSandboxSession() {
  try {
    localStorage.removeItem(META_KEY);
    localStorage.removeItem(LEAD_KEY);
    window.dispatchEvent(new Event('sandbox-session-changed'));
  } catch {}
}
